import clsx from "clsx"
import type { ReactNode } from "react"
import { Link, type LinkProps } from "@/components/Link/Link.component"

type CardProps = {
  readonly ariaLabel?: string
  readonly children: ReactNode
  readonly className?: string
  readonly elevated?: boolean
  readonly href?: null | string
  readonly variant?: "default" | "subtle"
  readonly size?: "small" | "medium" | "large" | "default"
  readonly position?: string
  readonly testId?: string
  readonly role?: string
  readonly onClick?: () => void
  readonly trackingModel?: LinkProps["trackingModel"]
}

export function Card({
  ariaLabel,
  children,
  className,
  href = null,
  variant = "default",
  elevated = false,
  position,
  testId = "card",
  size = "default",
  role,
  onClick,
  trackingModel,
}: CardProps) {
  const commonClasses = clsx(
    "flex flex-col justify-between rounded-xs",
    size === "default" && "gap-3",
    size === "small" && "gap-1",
    size === "medium" && "gap-2",
    size === "large" && "gap-4",
    variant === "subtle" && "border border-silver bg-whisper p-3",
    variant === "default" && "border border-silver bg-white p-3",
    elevated && "shadow-lg",
    href !== null && "cursor-pointer",
    href !== null && "hover:shadow-xl",
    className,
  )

  if (href !== null) {
    return (
      <Link
        ariaLabel={ariaLabel}
        className={commonClasses}
        data-k5a-pos={position}
        data-testid={testId}
        href={href}
        onClick={onClick}
        position={position}
        role={role}
        testId={testId}
        trackingModel={trackingModel}
      >
        {children}
      </Link>
    )
  }

  return (
    <article
      aria-label={ariaLabel}
      className={commonClasses}
      data-k5a-pos={position}
      data-testid={testId}
      role={role}
    >
      {children}
    </article>
  )
}
